// 2D (DOM) renderers for built-in widgets, plus a dispatcher that routes the
// `custom` kind to the sandboxed WidgetFrame.
import { useEffect, useState } from "react";
import { api, type NewsItem, type WeatherNow } from "@/core/lib/api-client";
import type { Widget } from "@/core/types";
import { WidgetFrame } from "@/widgets/host/WidgetFrame";

export function WidgetView({ widget }: { widget: Widget }) {
  switch (widget.kind) {
    case "clock":
      return <ClockWidget config={widget.config} />;
    case "weather":
      return <WeatherWidget config={widget.config} />;
    case "news":
      return <NewsWidget config={widget.config} />;
    case "rss":
      return <RssWidget config={widget.config} />;
    case "bookmarks":
      return <BookmarksWidget config={widget.config} />;
    case "search":
      return <SearchWidget config={widget.config} />;
    case "notes":
      return <NotesWidget config={widget.config} />;
    case "iframe":
      return <IframeWidget config={widget.config} />;
    case "custom":
      return widget.source ? (
        <WidgetFrame source={widget.source} config={widget.config} />
      ) : (
        <Empty label="Custom widget has no source" />
      );
  }
}

function Empty({ label }: { label: string }) {
  return <div className="p-3 text-sm opacity-60">{label}</div>;
}

function ClockWidget({ config }: { config: Record<string, unknown> }) {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  const h24 = config.format24h !== false;
  return (
    <div className="p-4 text-center">
      <div className="text-4xl font-semibold tabular-nums">
        {now.toLocaleTimeString(undefined, { hour12: !h24 })}
      </div>
      <div className="text-sm opacity-70">{now.toLocaleDateString()}</div>
    </div>
  );
}

function WeatherWidget({ config }: { config: Record<string, unknown> }) {
  const [w, setW] = useState<WeatherNow | null>(null);
  const lat = Number(config.lat ?? 40.71);
  const lon = Number(config.lon ?? -74.0);
  useEffect(() => {
    api.weather(lat, lon).then(setW).catch(() => setW(null));
  }, [lat, lon]);
  return (
    <div className="p-4">
      <div className="text-sm opacity-70">{String(config.label ?? "Weather")}</div>
      {w ? (
        <div className="mt-1 text-3xl font-semibold">{Math.round(w.temperature)}°</div>
      ) : (
        <div className="mt-1 text-sm opacity-50">Loading…</div>
      )}
    </div>
  );
}

function HeadlineList({ items }: { items: NewsItem[] }) {
  return (
    <ul className="space-y-2 p-3 text-sm">
      {items.slice(0, 8).map((n, i) => (
        <li key={i} className="truncate">
          <a href={n.url} target="_blank" rel="noreferrer" className="hover:underline">
            {n.title}
          </a>
        </li>
      ))}
    </ul>
  );
}

function NewsWidget({ config }: { config: Record<string, unknown> }) {
  const [items, setItems] = useState<NewsItem[]>([]);
  const topic = String(config.topic ?? "world");
  useEffect(() => {
    api.news(topic).then(setItems).catch(() => setItems([]));
  }, [topic]);
  return <HeadlineList items={items} />;
}

function RssWidget({ config }: { config: Record<string, unknown> }) {
  const [items, setItems] = useState<NewsItem[]>([]);
  const url = String(config.url ?? "");
  useEffect(() => {
    if (url) api.rss(url).then(setItems).catch(() => setItems([]));
  }, [url]);
  return url ? <HeadlineList items={items} /> : <Empty label="Set an RSS URL" />;
}

function BookmarksWidget({ config }: { config: Record<string, unknown> }) {
  const links = (config.links as { title: string; url: string }[]) ?? [];
  return (
    <div className="grid grid-cols-2 gap-2 p-3">
      {links.map((l, i) => (
        <a
          key={i}
          href={l.url}
          className="truncate rounded-lg bg-white/10 px-3 py-2 text-sm hover:bg-white/20"
        >
          {l.title}
        </a>
      ))}
      {links.length === 0 && <Empty label="No bookmarks yet" />}
    </div>
  );
}

function SearchWidget({ config }: { config: Record<string, unknown> }) {
  const engine = String(config.engine ?? "https://www.google.com/search?q=");
  return (
    <form
      className="p-3"
      onSubmit={(e) => {
        e.preventDefault();
        const q = new FormData(e.currentTarget).get("q");
        if (q) window.open(engine + encodeURIComponent(String(q)), "_blank");
      }}
    >
      <input
        name="q"
        placeholder="Search…"
        className="w-full rounded-full bg-white/15 px-4 py-2 outline-none placeholder:opacity-60"
      />
    </form>
  );
}

function NotesWidget({ config }: { config: Record<string, unknown> }) {
  return (
    <textarea
      defaultValue={String(config.text ?? "")}
      placeholder="Notes…"
      className="h-full w-full resize-none bg-transparent p-3 text-sm outline-none"
    />
  );
}

function IframeWidget({ config }: { config: Record<string, unknown> }) {
  const url = String(config.url ?? "");
  return url ? (
    <iframe title="embed" src={url} className="h-full w-full border-0" sandbox="allow-scripts allow-same-origin allow-popups" />
  ) : (
    <Empty label="Set an embed URL" />
  );
}
